import React from 'react'
import cx from 'classnames'
import { Checkbox, isMobile, Lozenge, l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { DraftIndicator } from 'apps/cms/components/DraftIndicator/DraftIndicator'
import { FlowContextMenu } from 'apps/cms/components/FlowContextMenu/FlowContextMenu'
import { Modified } from 'apps/cms/components/Flows/components/FlowCard/components/Modified/Modified'
import { Triggers } from 'apps/cms/components/Flows/components/FlowCard/components/Triggers/Triggers'
import { getCmsFlowBadge } from 'apps/cms/components/Flows/components/FlowCard/utils/badgeType'
import { useFlowCard } from 'apps/cms/components/Flows/hooks/useFlowCard'
import { useFlowCardDrag } from 'apps/cms/components/Flows/hooks/useFlowCardDrag'
import { FlowCardClickAction } from 'apps/cms/pages/ContentManagementSystem/lib/types'
import { contentManagementSelectors } from 'apps/cms/store'
import ProtectionStatus from 'apps/templates/components/ProtectionStatus'
import { FsFlow } from 'shared/api/requests/cms/schemas'
import { useDndRef } from 'utils/commonHooks'

import { getCtrValue, getRunsValue } from './lib/statistics'

import cm from './FlowCard.module.css'

interface ClassicFlowCardProps {
  flow: FsFlow
  allowSelect: boolean
  isFlowSelected: boolean
  onToggleFlowSelection: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function ClassicFlowCard({
  flow,
  allowSelect,
  isFlowSelected,
  onToggleFlowSelection,
}: ClassicFlowCardProps) {
  const flowId = flow.ns
  const isAnyFilterApplied = useAppSelector(contentManagementSelectors.getAnyFilterApplied)
  const {
    showFlowContextMenu,
    allowHoverFlowCard,
    allowHighlightActiveFlowCard,
    lastSelectedFlowId,
    flowCardClickAction,
  } = useAppSelector(contentManagementSelectors.getUi)
  const isFlowCardActive = lastSelectedFlowId === flowId && allowHighlightActiveFlowCard

  const {
    handleFlowCardClick,
    userCanEditFlow,
    isCardDisabled,
    isEasyBuilderFlow,
    isQuickAutomationFlow,
    cardUrl,
    cardActionAriaLabel,
    errorMessage,
  } = useFlowCard({ flow })

  const { connectDragSource } = useFlowCardDrag({ flow })
  const dragRef = useDndRef(connectDragSource)
  const badgeType = getCmsFlowBadge(flow)

  const statisticsColumnClassName = cx(
    cm.card__statistics,
    !isAnyFilterApplied && cm.card__statistics_accented,
  )

  return (
    <article
      ref={dragRef}
      className={cx(cm.card, {
        [cm.card_withoutContextMenu]: !userCanEditFlow || !showFlowContextMenu,
        [cm.card_disabled]: isCardDisabled,
        [cm.card_withHover]: allowHoverFlowCard,
        [cm.card_active]: isFlowCardActive,
      })}
      data-title-at="top"
      data-title={isCardDisabled && errorMessage && !isMobile ? errorMessage : undefined}
      data-test-id="flow-card"
    >
      {flowCardClickAction === FlowCardClickAction.OPEN_FLOW && (
        <a
          onClick={handleFlowCardClick}
          href={cardUrl}
          className={cm.card__mainActionButton}
          aria-label={cardActionAriaLabel}
        />
      )}

      {flowCardClickAction === FlowCardClickAction.SAVE_FLOW_ID_TO_STATE && (
        <button
          onClick={handleFlowCardClick}
          type="button"
          className={cm.card__mainActionButton}
          aria-label={cardActionAriaLabel}
        />
      )}

      {allowSelect && (
        <span className={cm.card__checkboxWrapper}>
          <Checkbox
            className={cm.card__interactableElement}
            checked={isFlowSelected}
            onChange={onToggleFlowSelection}
            ariaLabel={l.translate('Select flow')}
          />
        </span>
      )}

      <div className={cm.card__body}>
        <div className={cm.card__content}>
          <div className={cx(cm.card__title, 'text-ellipsis')}>
            {!isEasyBuilderFlow && flow.has_unpublished_changes && flow.has_published_content && (
              <DraftIndicator />
            )}

            <Lozenge size="large" type={badgeType} />

            <h1
              className={cx(
                cm.card__flowName,
                'text-ellipsis',
                'text-center',
                !isAnyFilterApplied && cm.card__flowName_accented,
              )}
            >
              {flow.name}
            </h1>
          </div>

          <div className={cm.card__triggerToggle} />

          <div className={cx(statisticsColumnClassName, cm.card__hiddenOnMobile)}>
            {getRunsValue(flow.stats.sent)}
          </div>

          <div className={cx(statisticsColumnClassName, cm.card__hiddenOnMobile)}>
            {getCtrValue(flow.stats)}
          </div>

          <div className="text-ellipsis">
            <Modified modified={flow.modified} />
          </div>

          <div className={cm.card__contextMenuColumn}>
            {userCanEditFlow && showFlowContextMenu && (
              <FlowContextMenu
                flowPath={flow.path}
                anchorClassName={cx(cm.card__interactableElement, cm.card__contextMenuButton)}
                anchorOpenedClassName={cm.card__contextMenuButton_opened}
                isEasyBuilderFlow={isEasyBuilderFlow}
                isQuickAutomationFlow={isQuickAutomationFlow}
              />
            )}

            {!isEasyBuilderFlow && flow.template_installation_access_data && (
              <ProtectionStatus
                className={cx(cm.card__interactableElement, cm.card__protectionStatus)}
                accessData={flow.template_installation_access_data}
              />
            )}
          </div>

          <div className={cx('text-body-sm', cm.card__mobileStatistics)}>
            <div className={cm.card__mobileStatisticsRow}>
              <span className="text-ellipsis">{l.translate('Runs')}</span>

              <span className={cx('flex-shrink-0', statisticsColumnClassName)}>
                {getRunsValue(flow.stats.sent)}
              </span>
            </div>

            <div className={cm.card__mobileStatisticsRow}>
              <span className="text-ellipsis">CTR</span>

              <span className={cx('flex-shrink-0', statisticsColumnClassName)}>
                {getCtrValue(flow.stats)}
              </span>
            </div>
          </div>
        </div>

        <Triggers
          triggers={flow.triggers}
          onClick={handleFlowCardClick}
          flowId={flowId}
          hideToggle={isEasyBuilderFlow}
        />
      </div>
    </article>
  )
}
